import { useS4LContext } from '@s4l/react-context'
import { useState, useEffect, useMemo } from 'react'

export enum BreakpointSizes {
  XS = 0,
  S = 320,
  M = 768,
  L = 1024,
}

export enum CustomMediaBreakpoints {
  Mobile = 'mobile',
  MobileL = 'mobile-l',
  Tablet = 'tablet',
  Desktop = 'desktop',
}

export enum Devices {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Tablet = 'tablet',
}

// Browser/OS detection utilities
const detectIsIos = (): boolean => {
  if (typeof window === 'undefined') return false

  const userAgent = window.navigator.userAgent.toLowerCase()

  return (
    /iphone|ipad|ipod/.test(userAgent) || (userAgent.includes('mac') && 'ontouchend' in document)
  )
}

const detectIsSafari = (): boolean => {
  if (typeof window === 'undefined') return false
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
}

const detectIsAppleDevice = (): boolean => {
  return detectIsIos() || detectIsSafari()
}

const detectIsApp = (): boolean => {
  return /sfl-mobile-app/i.test(navigator.userAgent)
}

export const useBrowser = () => {
  const { device: initialDevice } = useS4LContext()

  const initialDeviceMapper = {
    mobile: BreakpointSizes.S,
    tablet: BreakpointSizes.M,
    desktop: BreakpointSizes.L,
  }[initialDevice]

  const [activeBreakpoint, setActiveBreakpoint] = useState(initialDeviceMapper ?? BreakpointSizes.S)
  const [activeCustomMedia, setActiveCustomMedia] = useState<CustomMediaBreakpoints>(() => {
    if (initialDevice === 'tablet') return CustomMediaBreakpoints.Tablet
    if (initialDevice === 'desktop') return CustomMediaBreakpoints.Desktop
    return CustomMediaBreakpoints.Mobile
  })

  const device = useMemo(() => {
    if (activeBreakpoint <= BreakpointSizes.S) return Devices.Mobile
    if (activeBreakpoint <= BreakpointSizes.M) return Devices.Tablet
    return Devices.Desktop
  }, [activeBreakpoint])

  useEffect(() => {
    const mediaQueries = {
      mobile: window.matchMedia(`(max-width: 424px)`),
      mobileL: window.matchMedia(`(min-width: 425px) and (max-width: ${BreakpointSizes.M - 1}px)`),
      tablet: window.matchMedia(
        `(min-width: ${BreakpointSizes.M}px) and (max-width: ${BreakpointSizes.L - 1}px)`
      ),
      desktop: window.matchMedia(`(min-width: ${BreakpointSizes.L}px)`),
    }

    const handleMediaQuery = (e: MediaQueryListEvent | MediaQueryList) => {
      if (!e.matches) return

      let breakpoint: BreakpointSizes
      let customMediaBreakpoint: CustomMediaBreakpoints

      if (e === mediaQueries.mobile || e.media === mediaQueries.mobile.media) {
        breakpoint = BreakpointSizes.S
        customMediaBreakpoint = CustomMediaBreakpoints.Mobile
      } else if (e === mediaQueries.mobileL || e.media === mediaQueries.mobileL.media) {
        breakpoint = BreakpointSizes.S
        customMediaBreakpoint = CustomMediaBreakpoints.MobileL
      } else if (e === mediaQueries.tablet || e.media === mediaQueries.tablet.media) {
        breakpoint = BreakpointSizes.M
        customMediaBreakpoint = CustomMediaBreakpoints.Tablet
      } else {
        breakpoint = BreakpointSizes.L
        customMediaBreakpoint = CustomMediaBreakpoints.Desktop
      }

      setActiveBreakpoint(breakpoint)
      setActiveCustomMedia(customMediaBreakpoint)
    }

    Object.values(mediaQueries).forEach(mq => {
      mq.addEventListener('change', handleMediaQuery)
      handleMediaQuery(mq)
    })

    return () => {
      Object.values(mediaQueries).forEach(mq => {
        mq.removeEventListener('change', handleMediaQuery)
      })
    }
  }, [])

  const isAppleDevice = useMemo(() => detectIsAppleDevice(), [])
  const isApp = useMemo(() => detectIsApp(), [])

  return {
    activeBreakpoint,
    activeCustomMedia,
    device,
    isAppleDevice,
    isIOSApp: isApp && isAppleDevice,
    isAndroidApp: isApp && !isAppleDevice,
  }
}
